import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import type { ProductLead } from "@/types/productLeads.types";
import {
  X,
  Copy,
  Check,
  IndianRupee,
  Calendar,
  Clock,
  Briefcase,
  User,
  Phone,
  Mail,
  Building2,
  ExternalLink,
  Shield,
  Layers,
  ChevronDown,
  ChevronUp,
  Code,
  FileText,
} from "lucide-react";
import styles from "./LoanExtraFieldsDrawer.module.css";

interface Props {
  lead: ProductLead | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Robust check if a lead belongs to Loans (Personal Loan, Business Loan, etc.)
 */
export const isLoanLead = (lead: ProductLead | null | undefined): boolean => {
  if (!lead) return false;

  const category = (lead.product_category || "").toLowerCase();
  if (category.includes("loan") || category.includes("lap")) return true;

  const productId = (lead.product_id || "").toLowerCase();
  if (
    productId.endsWith("_pl") ||
    productId.endsWith("_bl") ||
    productId.includes("loan") ||
    productId.includes("_lap") ||
    productId.includes("pl_") ||
    productId.includes("bl_")
  ) {
    return true;
  }

  const extra = (lead.extra_fields && typeof lead.extra_fields === "object" ? lead.extra_fields : {}) as Record<string, any>;
  if (
    extra.loanAmount !== undefined ||
    extra.loanTenure !== undefined ||
    extra.loan_amount !== undefined ||
    extra.loan_tenure !== undefined ||
    lead.loanAmount !== undefined ||
    lead.loanTenure !== undefined
  ) {
    return true;
  }

  return false;
};

// Standard system keys to exclude from dynamic extra_fields extraction
const SYSTEM_KEYS = new Set([
  "id",
  "lead_id",
  "user_id",
  "agent_id",
  "product_id",
  "product_code",
  "product_category",
  "status",
  "source",
  "external_application_id",
  "description",
  "redirect_link",
  "mis_token",
  "mis_status",
  "customer_name",
  "customer_mob_num",
  "customer_email",
  "created_at",
  "updated_at",
  "total_records",
  "extra_fields",
  "agent_name",
  "agent_mob_num",
  "agent_email",
  "parent_agent_name",
  "parent_agent_mob_num",
  "affiliate_code",
  "tracking_id",
  "token",
  "mis_received_at",
  "mis_ingested_at",
  "stage",
  "evaluation_id",
  "sub",
]);

/**
 * Extracts and unifies all extra_fields from a lead, whether stored
 * in lead.extra_fields (object or JSON string) or flattened onto lead.
 */
export const extractExtraFields = (lead: ProductLead | null | undefined): Record<string, any> => {
  if (!lead) return {};
  let fields: Record<string, any> = {};

  if (typeof lead.extra_fields === "string") {
    try {
      fields = JSON.parse(lead.extra_fields);
    } catch {
      fields = {};
    }
  } else if (lead.extra_fields && typeof lead.extra_fields === "object") {
    fields = { ...lead.extra_fields };
  }

  // Also include any custom/flattened non-system properties present directly on lead
  Object.keys(lead).forEach((key) => {
    if (
      !SYSTEM_KEYS.has(key) &&
      fields[key] === undefined &&
      lead[key] !== undefined &&
      lead[key] !== null &&
      lead[key] !== ""
    ) {
      fields[key] = lead[key];
    }
  });

  return fields;
};

// Currency formatter for INR
export const formatCurrency = (val: unknown): string => {
  if (val === null || val === undefined || val === "") return "-";
  const num = Number(val);
  if (isNaN(num)) return String(val);
  return `₹${num.toLocaleString("en-IN")}`;
};

// Date Formatter
const formatDate = (val: unknown): string => {
  if (!val) return "-";
  try {
    const d = new Date(val as string);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return String(val);
  }
};

// Calculate age from DOB
const calculateAge = (dobVal: unknown): number | null => {
  if (!dobVal) return null;
  try {
    const birthDate = new Date(dobVal as string);
    if (isNaN(birthDate.getTime())) return null;
    const diffMs = Date.now() - birthDate.getTime();
    const ageDt = new Date(diffMs);
    return Math.abs(ageDt.getUTCFullYear() - 1970);
  } catch {
    return null;
  }
};

// Humanize field keys (e.g. loanAmount -> Loan Amount, source_id -> Source Id)
export const formatFieldLabel = (key: string): string => {
  // Handle snake_case and camelCase
  const result = key
    .replace(/([A-Z])/g, " $1")
    .replace(/[_-]+/g, " ")
    .trim();
  return result
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

export const LoanExtraFieldsDrawer: React.FC<Props> = ({ lead, isOpen, onClose }) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [showRawJson, setShowRawJson] = useState(false);

  // Close on Escape key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) {
        onClose();
      }
    };
    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  const copyToClipboard = (text: string, keyName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedKey(keyName);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  const extraFields = useMemo(() => extractExtraFields(lead), [lead]);

  // Resolved Customer Name
  const customerName = useMemo(() => {
    if (!lead) return "Lead Details";
    const resolved = (lead.customer_name ?? "") as string;
    if (resolved.trim()) return resolved.trim();
    const first = (extraFields.firstName ?? lead.firstName ?? "") as string;
    const last = (extraFields.lastName ?? lead.lastName ?? "") as string;
    const combined = [first, last].filter(Boolean).join(" ").trim();
    return combined || "Loan Applicant";
  }, [lead, extraFields]);

  if (!lead && !isOpen) return null;

  // Lead status styling
  const getStatusClass = (status?: string) => {
    const s = (status || "").toUpperCase();
    if (s === "REJECTED") return styles.statusRejected;
    if (["COMPLETED", "DISBURSED", "ACTIVATED"].includes(s)) return styles.statusDisbursed;
    if (["IN_PROGRESS", "INITIATED"].includes(s)) return styles.statusInProgress;
    return styles.statusClicked;
  };

  // Employment badge styling
  const getEmploymentBadgeClass = (emp?: string) => {
    const e = (emp || "").toLowerCase();
    if (e.includes("salaried")) return styles.badgeSalaried;
    if (e.includes("business")) return styles.badgeBusiness;
    if (e.includes("self")) return styles.badgeSelfEmployed;
    return styles.badgeNeutral;
  };

  // Pre-mapped standard fields to avoid duplication in "Dynamic / Other" section
  const knownKeys = new Set([
    "loanAmount",
    "loan_amount",
    "amount",
    "loanTenure",
    "loan_tenure",
    "tenure",
    "income",
    "monthly_income",
    "monthlyIncome",
    "employment",
    "employment_type",
    "employmentType",
    "firstName",
    "lastName",
    "fname",
    "lname",
    "dob",
    "date_of_birth",
    "email",
    "mobile",
    "phone",
    "vendor",
    "partner",
    "origin",
    "source_id",
  ]);

  // Filter remaining dynamic fields
  const dynamicExtraKeys = Object.keys(extraFields).filter(
    (key) => !knownKeys.has(key) && extraFields[key] !== null && extraFields[key] !== undefined && extraFields[key] !== ""
  );

  const loanAmount = extraFields.loanAmount ?? extraFields.loan_amount ?? extraFields.amount;
  const loanTenure = extraFields.loanTenure ?? extraFields.loan_tenure ?? extraFields.tenure;
  const income = extraFields.income ?? extraFields.monthly_income ?? extraFields.monthlyIncome;
  const employment = extraFields.employment ?? extraFields.employment_type ?? extraFields.employmentType;
  const dob = extraFields.dob ?? extraFields.date_of_birth;
  const age = calculateAge(dob);
  const email = extraFields.email ?? lead?.customer_email ?? lead?.email;
  const mobile = extraFields.mobile ?? lead?.customer_mob_num ?? lead?.mobile;
  const vendor = extraFields.vendor ?? extraFields.partner;
  const origin = extraFields.origin;
  const sourceId = extraFields.source_id;
  const externalAppId = lead?.external_application_id;

  const resumeLink = (lead?.redirect_link || lead?.description) as string | undefined;

  const copyAllSummary = () => {
    if (!lead) return;
    const summaryLines = [
      `Customer Name: ${customerName}`,
      `Product: ${lead.product_id || "-"}`,
      `Category: ${lead.product_category || "Personal Loan"}`,
      `Status: ${lead.status || "-"}`,
      loanAmount ? `Loan Amount: ${formatCurrency(loanAmount)}` : null,
      loanTenure ? `Loan Tenure: ${loanTenure} Months` : null,
      income ? `Income: ${formatCurrency(income)}` : null,
      employment ? `Employment: ${employment}` : null,
      dob ? `DOB: ${dob}` : null,
      mobile ? `Mobile: ${mobile}` : null,
      email ? `Email: ${email}` : null,
      vendor ? `Vendor: ${vendor}` : null,
      externalAppId ? `Application ID: ${externalAppId}` : null,
      `Lead ID: ${lead.lead_id}`,
    ]
      .filter(Boolean)
      .join("\n");

    copyToClipboard(summaryLines, "all_summary");
  };

  // Helper to render dynamic values cleanly
  const renderDynamicValue = (key: string, val: unknown) => {
    if (val === null || val === undefined || val === "") {
      return <span className={styles.emptyDash}>-</span>;
    }

    if (typeof val === "boolean") {
      return (
        <span className={val ? styles.badgeSuccess : styles.badgeMuted}>
          {val ? "Yes" : "No"}
        </span>
      );
    }

    if (
      typeof val === "string" &&
      (val.startsWith("http://") || val.startsWith("https://"))
    ) {
      return (
        <a
          href={val}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.linkValue}
        >
          <span>Open Link</span>
          <ExternalLink size={12} />
        </a>
      );
    }

    const lowerKey = key.toLowerCase();
    if (
      (lowerKey.includes("amount") ||
        lowerKey.includes("income") ||
        lowerKey.includes("salary") ||
        lowerKey.includes("emi") ||
        lowerKey.includes("fee") ||
        lowerKey.includes("cost") ||
        lowerKey.includes("turnover")) &&
      !isNaN(Number(val))
    ) {
      return <span className={styles.fieldValue}>{formatCurrency(val)}</span>;
    }

    const strVal = String(val);
    const isLikelyCode =
      lowerKey.includes("id") ||
      lowerKey.includes("pan") ||
      lowerKey.includes("aadhaar") ||
      lowerKey.includes("code") ||
      lowerKey.includes("cibil") ||
      lowerKey.includes("pincode") ||
      lowerKey.includes("pin");

    if (isLikelyCode && strVal.length > 0 && strVal.length <= 40) {
      return (
        <div className={styles.fieldValueRow}>
          <span className={styles.fieldValueMonospace}>{strVal}</span>
          <button
            type="button"
            className={styles.copyActionBtn}
            onClick={() => copyToClipboard(strVal, key)}
            title={`Copy ${formatFieldLabel(key)}`}
          >
            {copiedKey === key ? (
              <Check size={12} color="#16a34a" />
            ) : (
              <Copy size={12} />
            )}
          </button>
        </div>
      );
    }

    if (typeof val === "object") {
      if (Array.isArray(val)) {
        return (
          <span className={styles.fieldValue}>
            {val
              .map((item) =>
                typeof item === "object" ? JSON.stringify(item) : String(item)
              )
              .join(", ")}
          </span>
        );
      }
      return (
        <pre className={styles.fieldValueMonospace}>
          {JSON.stringify(val, null, 2)}
        </pre>
      );
    }

    return <span className={styles.fieldValue}>{strVal}</span>;
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`${styles.backdrop} ${isOpen ? styles.backdropOpen : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <aside
        className={`${styles.drawer} ${isOpen ? styles.drawerOpen : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Loan Extra Details"
      >
        {lead && (
          <>
            {/* Header */}
            <div className={styles.header}>
              <div className={styles.headerTopRow}>
                <div className={styles.badgesGroup}>
                  <span className={styles.categoryBadge}>
                    <Layers size={13} />
                    {lead.product_category || "Loan Lead"}
                  </span>
                  {lead.status && (
                    <span className={`${styles.statusBadge} ${getStatusClass(lead.status)}`}>
                      <Shield size={12} />
                      {lead.status}
                    </span>
                  )}
                  {lead.mis_status && (
                    <span
                      className={`${styles.statusBadge} ${styles.statusMis}`}
                      title="MIS Vendor Status"
                    >
                      MIS: {lead.mis_status}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className={styles.closeButton}
                  title="Close (Esc)"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              <div className={styles.titleArea}>
                <h2 className={styles.customerName}>{customerName}</h2>
                <p className={styles.productSubtitle}>
                  <span>Product:</span>
                  <span className={styles.productCodePill}>{lead.product_id}</span>
                  {vendor && (
                    <>
                      <span>• Partner:</span>
                      <strong>{vendor}</strong>
                    </>
                  )}
                </p>
              </div>

              <div className={styles.metaBar}>
                <div className={styles.metaItem}>
                  <span>Lead ID:</span>
                  <span className={styles.fieldValueMonospace}>
                    {lead.lead_id && lead.lead_id.length > 12
                      ? `${lead.lead_id.slice(0, 12)}...`
                      : lead.lead_id}
                  </span>
                  <button
                    type="button"
                    className={styles.copyInlineBtn}
                    onClick={() => copyToClipboard(lead.lead_id, "lead_id")}
                    title="Copy full Lead ID"
                  >
                    {copiedKey === "lead_id" ? <Check size={12} color="#16a34a" /> : <Copy size={12} />}
                    {copiedKey === "lead_id" ? "Copied" : "Copy"}
                  </button>
                </div>
                {lead.created_at && (
                  <div className={styles.metaItem}>
                    <span>Created:</span>
                    <strong>{formatDate(lead.created_at)}</strong>
                  </div>
                )}
              </div>
            </div>

            {/* Scrollable Content Body */}
            <div className={styles.content}>
              {/* Key Highlights Hero Cards */}
              {(loanAmount || loanTenure || income || employment) && (
                <div className={styles.heroMetricsGrid}>
                  {loanAmount && (
                    <div className={styles.metricCard}>
                      <div className={styles.metricHeader}>
                        <IndianRupee size={15} className={styles.metricIcon} />
                        <span>Requested Loan</span>
                      </div>
                      <div className={styles.metricValuePrimary}>{formatCurrency(loanAmount)}</div>
                    </div>
                  )}

                  {loanTenure && (
                    <div className={styles.metricCard}>
                      <div className={styles.metricHeader}>
                        <Clock size={15} className={styles.metricIcon} />
                        <span>Loan Tenure</span>
                      </div>
                      <div className={styles.metricValueSecondary}>
                        {loanTenure} Months
                        <span className={styles.metricHint}>
                          ({(Number(loanTenure) / 12).toFixed(1).replace(/\.0$/, "")} yrs)
                        </span>
                      </div>
                    </div>
                  )}

                  {income && (
                    <div className={styles.metricCard}>
                      <div className={styles.metricHeader}>
                        <Briefcase size={15} className={styles.metricIcon} />
                        <span>Monthly Income</span>
                      </div>
                      <div className={styles.metricValueSecondary}>
                        {formatCurrency(income)}
                        <span className={styles.metricHint}>/ mo</span>
                      </div>
                    </div>
                  )}

                  {employment && (
                    <div className={styles.metricCard}>
                      <div className={styles.metricHeader}>
                        <User size={15} className={styles.metricIcon} />
                        <span>Employment Type</span>
                      </div>
                      <div className={styles.metricBadgeWrap}>
                        <span className={`${styles.metricBadge} ${getEmploymentBadgeClass(String(employment))}`}>
                          {String(employment)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Section 1: Loan & Financial Parameters */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}>
                    <IndianRupee size={15} className={styles.sectionTitleIcon} />
                    Loan & Financial Parameters
                  </h3>
                </div>
                <div className={styles.fieldsGrid}>
                  <div className={styles.fieldItem}>
                    <span className={styles.fieldLabel}>Loan Amount</span>
                    <span className={styles.fieldValue}>{formatCurrency(loanAmount)}</span>
                  </div>

                  <div className={styles.fieldItem}>
                    <span className={styles.fieldLabel}>Tenure</span>
                    <span className={styles.fieldValue}>
                      {loanTenure ? `${loanTenure} Months` : "-"}
                    </span>
                  </div>

                  <div className={styles.fieldItem}>
                    <span className={styles.fieldLabel}>Declared Income</span>
                    <span className={styles.fieldValue}>{formatCurrency(income)}</span>
                  </div>

                  <div className={styles.fieldItem}>
                    <span className={styles.fieldLabel}>Employment Profile</span>
                    <span className={styles.fieldValue}>{employment ? String(employment) : "-"}</span>
                  </div>
                </div>
              </div>

              {/* Section 2: Applicant Information */}
              <div className={styles.sectionCard}>
                <div className={styles.sectionHeader}>
                  <h3 className={styles.sectionTitle}>
                    <User size={15} className={styles.sectionTitleIcon} />
                    Applicant Information
                  </h3>
                </div>
                <div className={styles.fieldsGrid}>
                  <div className={`${styles.fieldItemFull} ${styles.fieldItem}`}>
                    <span className={styles.fieldLabel}>Full Name</span>
                    <span className={`${styles.fieldValue} ${styles.fieldValueStrong}`}>
                      {customerName}
                    </span>
                  </div>

                  <div className={styles.fieldItem}>
                    <span className={styles.fieldLabel}>Date of Birth</span>
                    <div className={styles.fieldValueRow}>
                      <Calendar size={13} color="#64748b" />
                      <span className={styles.fieldValue}>
                        {formatDate(dob)} {age ? `(${age} yrs)` : ""}
                      </span>
                    </div>
                  </div>

                  <div className={styles.fieldItem}>
                    <span className={styles.fieldLabel}>Mobile Number</span>
                    <div className={styles.fieldValueRow}>
                      <Phone size={13} color="#64748b" />
                      <span className={styles.fieldValue}>{mobile ? String(mobile) : "-"}</span>
                      {mobile && (
                        <button
                          type="button"
                          className={styles.copyActionBtn}
                          onClick={() => copyToClipboard(String(mobile), "mobile")}
                          title="Copy Mobile Number"
                        >
                          {copiedKey === "mobile" ? <Check size={12} color="#16a34a" /> : <Copy size={12} />}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className={`${styles.fieldItemFull} ${styles.fieldItem}`}>
                    <span className={styles.fieldLabel}>Email Address</span>
                    <div className={styles.fieldValueRow}>
                      <Mail size={13} color="#64748b" />
                      <span className={styles.fieldValue}>{email ? String(email) : "-"}</span>
                      {email && (
                        <button
                          type="button"
                          className={styles.copyActionBtn}
                          onClick={() => copyToClipboard(String(email), "email")}
                          title="Copy Email"
                        >
                          {copiedKey === "email" ? <Check size={12} color="#16a34a" /> : <Copy size={12} />}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 3: Vendor & Application Origin */}
              {(vendor || externalAppId || origin || sourceId || lead.source || lead.agent_name) && (
                <div className={styles.sectionCard}>
                  <div className={styles.sectionHeader}>
                    <h3 className={styles.sectionTitle}>
                      <Building2 size={15} className={styles.sectionTitleIcon} />
                      Vendor & Origination Details
                    </h3>
                  </div>
                  <div className={styles.fieldsGrid}>
                    {vendor && (
                      <div className={styles.fieldItem}>
                        <span className={styles.fieldLabel}>Partner / Vendor</span>
                        <span className={`${styles.fieldValue} ${styles.fieldValuePartner}`}>
                          {String(vendor)}
                        </span>
                      </div>
                    )}

                    {externalAppId && (
                      <div className={styles.fieldItemFull}>
                        <span className={styles.fieldLabel}>External App ID</span>
                        <div className={styles.fieldValueRow}>
                          <span className={styles.fieldValueMonospace}>{String(externalAppId)}</span>
                          <button
                            type="button"
                            className={styles.copyActionBtn}
                            onClick={() => copyToClipboard(String(externalAppId), "ext_app_id")}
                            title="Copy External Application ID"
                          >
                            {copiedKey === "ext_app_id" ? <Check size={12} color="#16a34a" /> : <Copy size={12} />}
                          </button>
                        </div>
                      </div>
                    )}

                    {lead.source && (
                      <div className={styles.fieldItem}>
                        <span className={styles.fieldLabel}>Ingress Source</span>
                        <span className={styles.fieldValue}>{String(lead.source)}</span>
                      </div>
                    )}

                    {origin && (
                      <div className={styles.fieldItem}>
                        <span className={styles.fieldLabel}>Origin Tag</span>
                        <span className={styles.fieldValueMonospace}>{String(origin)}</span>
                      </div>
                    )}

                    {sourceId && (
                      <div className={styles.fieldItem}>
                        <span className={styles.fieldLabel}>Source ID</span>
                        <span className={styles.fieldValueMonospace}>{String(sourceId)}</span>
                      </div>
                    )}

                    {lead.agent_name && (
                      <div className={styles.fieldItem}>
                        <span className={styles.fieldLabel}>Sub Agent</span>
                        <span className={styles.fieldValue}>{String(lead.agent_name)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Section 4: All Additional Dynamic Extra Fields */}
              {dynamicExtraKeys.length > 0 && (
                <div className={styles.sectionCard}>
                  <div className={styles.sectionHeader}>
                    <h3 className={styles.sectionTitle}>
                      <FileText size={15} className={styles.sectionTitleIcon} />
                      Additional Dynamic Details ({dynamicExtraKeys.length})
                    </h3>
                  </div>
                  <div className={styles.fieldsGrid}>
                    {dynamicExtraKeys.map((key) => {
                      const val = extraFields[key];
                      const isLong =
                        typeof val === "object" ||
                        (typeof val === "string" && val.length > 25) ||
                        key.toLowerCase().includes("address") ||
                        key.toLowerCase().includes("desc") ||
                        key.toLowerCase().includes("reason") ||
                        key.toLowerCase().includes("url") ||
                        key.toLowerCase().includes("link") ||
                        key.toLowerCase().includes("comment") ||
                        key.toLowerCase().includes("message");

                      return (
                        <div
                          key={key}
                          className={isLong ? styles.fieldItemFull : styles.fieldItem}
                        >
                          <span className={styles.fieldLabel}>{formatFieldLabel(key)}</span>
                          {renderDynamicValue(key, val)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Section 5: Raw JSON Inspection Accordion */}
              <div className={styles.sectionCard}>
                <button
                  type="button"
                  className={styles.accordionHeader}
                  onClick={() => setShowRawJson(!showRawJson)}
                >
                  <span className={styles.accordionTitle}>
                    <Code size={15} className={styles.sectionTitleIcon} />
                    Inspect Raw extra_fields JSON
                  </span>
                  {showRawJson ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>

                {showRawJson && (
                  <div>
                    <div className={styles.jsonToolbar}>
                      <button
                        type="button"
                        className={`${styles.copyInlineBtn} ${styles.copyInlineBtnDark}`}
                        onClick={() => copyToClipboard(JSON.stringify(extraFields, null, 2), "raw_json")}
                      >
                        {copiedKey === "raw_json" ? <Check size={12} color="#4ade80" /> : <Copy size={12} />}
                        {copiedKey === "raw_json" ? "JSON Copied!" : "Copy JSON"}
                      </button>
                    </div>
                    <pre className={styles.jsonViewer}>
                      {JSON.stringify(extraFields, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            </div>

            {/* Sticky Footer */}
            <div className={styles.footer}>
              <div className={styles.footerLeft}>
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={copyAllSummary}
                  title="Copy formatted summary of all loan details"
                >
                  {copiedKey === "all_summary" ? <Check size={14} color="#16a34a" /> : <Copy size={14} />}
                  {copiedKey === "all_summary" ? "Copied All!" : "Copy Summary"}
                </button>
              </div>

              <div className={styles.footerRight}>
                {resumeLink && (
                  <a
                    href={resumeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.primaryActionBtn}
                  >
                    <ExternalLink size={15} />
                    Resume Journey
                  </a>
                )}
                <button
                  type="button"
                  className={styles.secondaryBtn}
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </div>
          </>
        )}
      </aside>
    </>,
    document.body
  );
};
